import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const auditLogService = {
  /**
   * Fetch audit logs with advanced filtering
   */
  async getAuditLogs(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles(full_name, email, role)
        `)?.order('timestamp', { ascending: false });

      // Apply filters
      if (filters?.tableName) {
        query = query?.eq('table_name', filters?.tableName);
      }

      if (filters?.action) {
        query = query?.eq('action', filters?.action);
      }

      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      if (filters?.startDate) {
        query = query?.gte('timestamp', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('timestamp', filters?.endDate);
      }

      if (filters?.recordId) {
        query = query?.eq('record_id', filters?.recordId);
      }

      // Pagination
      const limit = filters?.limit || 50;
      const offset = filters?.offset || 0;
      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAuditLogs:', error);
          return { data: [], error: null };
        }
        throw error;
      }

      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: [], error };
    }
  },

  /**
   * Get audit log count for pagination
   */
  async getAuditLogCount(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select('*', { count: 'exact', head: true });

      if (filters?.tableName) {
        query = query?.eq('table_name', filters?.tableName);
      }

      if (filters?.action) {
        query = query?.eq('action', filters?.action);
      }

      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      if (filters?.startDate) {
        query = query?.gte('timestamp', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('timestamp', filters?.endDate);
      }

      const { count, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          return { count: 0, error: null };
        }
        throw error;
      }

      return { count, error: null };
    } catch (error) {
      console.error('Error getting audit log count:', error);
      return { count: 0, error };
    }
  },

  /**
   * Get unique users who have audit entries
   */
  async getAuditUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.order('full_name');

      if (error) {
        if (isSchemaError(error)) {
          return { data: [], error: null };
        }
        throw error;
      }

      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching audit users:', error);
      return { data: [], error };
    }
  },

  /**
   * Export audit logs to CSV format
   */
  async exportAuditLogs(filters = {}) {
    try {
      const { data, error } = await this.getAuditLogs({ ...filters, limit: 10000 });

      if (error) throw error;

      // Convert to CSV
      const headers = ['Timestamp', 'User', 'Entity Type', 'Action', 'Record ID', 'Changes', 'IP Address'];
      const rows = data?.map(log => [
        new Date(log?.timestamp)?.toLocaleString(),
        log?.user?.fullName || 'System',
        log?.tableName,
        log?.action,
        log?.recordId,
        JSON.stringify(log?.newData || {}),
        log?.ipAddress || 'N/A'
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      return { data: csvContent, error: null };
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      return { data: null, error };
    }
  },

  /**
   * Get severity level based on action and table
   */
  getSeverityLevel(action, tableName) {
    if (action === 'delete') return 'critical';
    if (action === 'status_change' && tableName === 'work_orders') return 'high';
    if (action === 'update' && ['quotation_items', 'work_orders']?.includes(tableName)) return 'medium';
    return 'low';
  },

  /**
   * Format change description for display
   */
  formatChangeDescription(log) {
    const action = log?.action;
    const tableName = log?.tableName;
    const oldData = log?.oldData || {};
    const newData = log?.newData || {};

    if (action === 'create') {
      return `Created new ${tableName?.replace('_', ' ')}`;
    }

    if (action === 'delete') {
      return `Deleted ${tableName?.replace('_', ' ')}`;
    }

    if (action === 'status_change') {
      return `Changed status from "${oldData?.estado_ot || oldData?.status}" to "${newData?.estado_ot || newData?.status}"`;
    }

    if (action === 'update') {
      const changes = Object.keys(newData)?.filter(key => 
        JSON.stringify(oldData?.[key]) !== JSON.stringify(newData?.[key])
      );
      return `Updated ${changes?.length} field(s): ${changes?.join(', ')}`;
    }

    return `${action} on ${tableName}`;
  }
};

export default auditLogService;